import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { sumBy, uniqueId } from 'lodash';

import { DashboardService } from '@/services';
import './index.scss';
import { calculateCreditSummaryByKindPercent } from '@/helpers/dashboard';
import RadialChart from '@/components/RadialChart';

function CreditSummary() {
  const { t } = useTranslation();
  const [creditSummary, setCreditSummary] = useState([
    {
      title: t(
        'dashboard.summary_section.credit_summary.credit_items.credit_balance',
      ),
      amount: 0,
      color: 'linear-gradient(269.66deg, #A45CFF 0.16%, #3C0CFF 99.76%)',
      percent: 0,
    },
    {
      title: t(
        'dashboard.summary_section.credit_summary.credit_items.used_credit',
      ),
      amount: 0,
      color: '#000000',
      percent: 0,
    },
    {
      title: t(
        'dashboard.summary_section.credit_summary.credit_items.blocking_credit',
      ),
      amount: 0,
      color: '#73D13D',
      percent: 0,
    },
    {
      title: t(
        'dashboard.summary_section.credit_summary.credit_items.confirm_pending_credit',
      ),
      amount: 0,
      color: '#FFC53D',
      percent: 0,
    },
    {
      title: t(
        'dashboard.summary_section.credit_summary.credit_items.payment_pending_credit',
      ),
      amount: 0,
      color: '#FF7A45',
      percent: 0,
    },
  ]);
  const [dataRadialChart, setDataRadialChart] = useState([
    {
      radius: 84,
      color: '#9254DE',
      progress: 0,
    },
    {
      radius: 69,
      color: '#000000',
      progress: 0,
    },
    {
      radius: 54,
      color: '#73D13D',
      progress: 0,
    },
    {
      radius: 39,
      color: '#FFC53D',
      progress: 0,
    },
    {
      radius: 24,
      color: '#FF7A45',
      progress: 0,
    },
  ]);
  const [totalCredit, setTotalCredit] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCreditSummary = async () => {
    const { success, data } = await DashboardService.getCreditSummary();

    if (success) {
      const dataByPercent = calculateCreditSummaryByKindPercent(data);

      setCreditSummary([
        {
          title: t(
            'dashboard.summary_section.credit_summary.credit_items.credit_balance',
          ),
          amount: data.creditBalance,
          color: 'linear-gradient(269.66deg, #A45CFF 0.16%, #3C0CFF 99.76%)',
          percent: dataByPercent.creditBalance,
        },
        {
          title: t(
            'dashboard.summary_section.credit_summary.credit_items.used_credit',
          ),
          amount: data.usedCredit,
          color: '#000000',
          percent: dataByPercent.usedCredit,
        },
        {
          title: t(
            'dashboard.summary_section.credit_summary.credit_items.blocking_credit',
          ),
          amount: data.blockingCredit,
          color: '#73D13D',
          percent: dataByPercent.blockingCredit,
        },
        {
          title: t(
            'dashboard.summary_section.credit_summary.credit_items.confirm_pending_credit',
          ),
          amount: data.confirmPendingCredit,
          color: '#FFC53D',
          percent: dataByPercent.confirmPendingCredit,
        },
        {
          title: t(
            'dashboard.summary_section.credit_summary.credit_items.payment_pending_credit',
          ),
          amount: data.paymentPendingCredit,
          color: '#FF7A45',
          percent: dataByPercent.paymentPendingCredit,
        },
      ]);
      setDataRadialChart([
        {
          radius: 90,
          color: '#9254DE',
          progress: dataByPercent.creditBalance,
        },
        {
          radius: 75,
          color: '#000000',
          progress: dataByPercent.usedCredit,
        },
        {
          radius: 60,
          color: '#73D13D',
          progress: dataByPercent.blockingCredit,
        },
        {
          radius: 45,
          color: '#FFC53D',
          progress: dataByPercent.confirmPendingCredit,
        },
        {
          radius: 30,
          color: '#FF7A45',
          progress: dataByPercent.paymentPendingCredit,
        },
      ]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCreditSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const total = sumBy(creditSummary, (item) => item.amount * 1);
    setTotalCredit(total);
  }, [creditSummary]);

  return (
    <div
      className={`card credit-summary-container${loading ? ' --loading' : ''}`}
    >
      {loading && <Spin spinning />}
      {!loading && (
        <>
          <div className="credit-summary__info">
            <div className="credit-summary__title">
              {t('dashboard.summary_section.credit_summary.title')}
            </div>
            <div className="credit-summary__amount">
              {totalCredit.toLocaleString()}
            </div>
            <div className="credit-summary__statistic">
              <div className="credit-summary__statistic__annotation">
                {creditSummary.map((item) => (
                  <div
                    className="credit-summary__statistic__annotation-item"
                    key={uniqueId()}
                  >
                    <div
                      className="point"
                      style={{ background: `${item.color}` }}
                    />
                    <div className="item-title">{item.title}</div>
                    <div className="item-amount">
                      {item.amount.toLocaleString()}
                    </div>
                    <div className="item-percent">{item.percent || 0}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="credit-summary__right">
            <RadialChart data={dataRadialChart} />
          </div>
        </>
      )}
    </div>
  );
}

export default CreditSummary;
